/**
 * Accessibility Statement Page
 *
 * Declares RetireEZ's commitment to digital accessibility.
 * Contains the six required sections from FS-016 RWP-F:
 *   1. Commitment Statement
 *   2. Conformance Status (WCAG 2.1 Level AA)
 *   3. Accessibility Features
 *   4. Known Limitations
 *   5. Feedback
 *   6. Compatibility
 *
 * Uses the shared LegalLayout wrapper for consistent page structure.
 */

import { LegalLayout } from "../components/LegalLayout";

export default function AccessibilityStatement() {
  return (
    <LegalLayout
      title="Accessibility Statement"
      lastUpdated="Last Updated: February 13, 2026"
      id="accessibility-statement"
    >
      {/* 1. Commitment Statement */}
      <section>
        <h2>Our Commitment</h2>
        <p>
          RetireEZ is committed to ensuring digital accessibility for people
          with disabilities. We continually improve the user experience for
          everyone and apply the relevant accessibility standards.
        </p>
      </section>

      {/* 2. Conformance Status */}
      <section>
        <h2>Conformance Status</h2>
        <p>
          This website aims to conform to <strong>WCAG 2.1 Level AA</strong>.
          The Web Content Accessibility Guidelines (WCAG) define requirements
          for designers and developers to improve accessibility for people with
          disabilities. We regularly review our application against these
          standards and address gaps as they are identified.
        </p>
      </section>

      {/* 3. Accessibility Features */}
      <section>
        <h2>Accessibility Features</h2>
        <p>RetireEZ includes the following accessibility features:</p>
        <ul>
          <li>
            <strong>Keyboard navigation</strong> throughout the application —
            all interactive elements are reachable and operable via keyboard
          </li>
          <li>
            <strong>Screen reader</strong> compatible with ARIA landmarks,
            roles, and labels for assistive technology support
          </li>
          <li>
            <strong>Color contrast</strong> ratios meeting the 4.5:1 minimum for
            normal text and 3:1 for large text
          </li>
          <li>
            <strong>Resizable text</strong> without loss of functionality — the
            layout adapts gracefully up to 200% zoom
          </li>
          <li>
            <strong>Touch targets</strong> meeting the 44×44px minimum size for
            comfortable mobile interaction
          </li>
          <li>
            <strong>Reduced motion</strong> support via the
            prefers-reduced-motion media query — animations are minimised or
            disabled for users who prefer less motion
          </li>
        </ul>
      </section>

      {/* 4. Known Limitations */}
      <section>
        <h2>Known Limitations</h2>
        <p>
          Despite our best efforts, some areas may not yet be fully accessible:
        </p>
        <ul>
          <li>
            Some interactive charts may not convey all data points to screen
            readers. We provide equivalent tabular data as an alternative.
          </li>
          <li>
            PDF export functionality (planned) will need additional
            accessibility review once implemented.
          </li>
        </ul>
        <p>
          We are actively working to resolve these limitations. If you encounter
          an accessibility barrier not listed here, please let us know using the
          contact information below.
        </p>
      </section>

      {/* 5. Feedback */}
      <section>
        <div className="accessibility-feedback">
          <h2 className="accessibility-feedback-title">Feedback</h2>
          <p>
            We welcome your feedback on the accessibility of RetireEZ. If you
            encounter accessibility barriers or have suggestions for
            improvement, please contact us:
          </p>
          <p>
            Email:{" "}
            <a href="mailto:support@retire-ez.ca">support@retire-ez.ca</a>
          </p>
          <p>
            We aim to respond to accessibility feedback within 5 business days
            and to resolve reported issues as quickly as possible.
          </p>
        </div>
      </section>

      {/* 6. Compatibility */}
      <section>
        <h2>Compatibility</h2>
        <p>
          RetireEZ is designed to be compatible with the following assistive
          technologies:
        </p>
        <ul>
          <li>VoiceOver on macOS and iOS</li>
          <li>NVDA and JAWS on Windows</li>
          <li>TalkBack on Android</li>
        </ul>
        <p>
          The application is tested and optimised for the latest versions of
          Chrome, Firefox, Safari, and Edge. Older browsers may not support all
          accessibility features.
        </p>
      </section>
    </LegalLayout>
  );
}
